import React, { useState, useEffect } from "react";
import { Typography, Box, Snackbar, Alert } from "@mui/material";
import { Button, TextField, Container } from "@/components/atoms";
import { isValidPassword } from "@/utils/validators";
import { useChangePassword } from "@/hooks";
import { useNavigate } from "react-router-dom";

export const ChangePassword = () => {
  const navigate = useNavigate();

  const { data, loading, error, changePassword } = useChangePassword();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [currentPasswordError, setCurrentPasswordError] = useState<
    string | null
  >(null);
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<
    string | null
  >(null);

  const [isFormValid, setIsFormValid] = useState(false);

  const [snackBar, setSnackBar] = useState({
    state: false,
    msg: "",
    severity: "success" as "success" | "error",
  });

  useEffect(() => {
    setNewPasswordError(
      newPassword && !isValidPassword(newPassword)
        ? "Password must be at least 8 characters."
        : null,
    );

    setConfirmPasswordError(
      newPassword && confirmPassword && newPassword !== confirmPassword
        ? "Passwords do not match."
        : null,
    );

    setCurrentPasswordError(
      currentPassword.length === 0 ? "Current password is required." : null,
    );
  }, [currentPassword, newPassword, confirmPassword]);

  useEffect(() => {
    setIsFormValid(
      !currentPasswordError &&
        !newPasswordError &&
        !confirmPasswordError &&
        currentPassword !== "" &&
        newPassword !== "" &&
        confirmPassword !== "",
    );
  }, [currentPasswordError, newPasswordError, confirmPasswordError]);

  useEffect(() => {
    if (data?.success) {
      setSnackBar({
        state: true,
        msg: data.message || "Password changed successfully.",
        severity: "success",
      });

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");

      // Redirect to home after 2 seconds
      const timer = setTimeout(() => navigate("/"), 2000);
      return () => clearTimeout(timer);
    }
  }, [data, navigate]);

  useEffect(() => {
    if (error) {
      setSnackBar({
        state: true,
        msg: error,
        severity: "error",
      });
    }
  }, [error]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid) return;

    await changePassword({
      old_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
  };

  return (
    <Container
      componentName="change-password"
      sx={{
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Snackbar
        open={snackBar.state}
        autoHideDuration={6000}
        onClose={() => setSnackBar(prev => ({ ...prev, state: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackBar.severity}
          variant="filled"
          onClose={() => setSnackBar(prev => ({ ...prev, state: false }))}
        >
          {snackBar.msg}
        </Alert>
      </Snackbar>

      <Box justifyContent="center" display="flex" sx={{ margin: 2 }}>
        <Typography variant="h4" gutterBottom>
          Change Password
        </Typography>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          width: "100%",
          maxWidth: "400px",
          border: "none",
        }}
      >
        <TextField
          componentName="current-password"
          variant="outlined"
          type="password"
          label="Current Password"
          value={currentPassword}
          onChange={e => setCurrentPassword(e.target.value)}
          required
          error={!!currentPasswordError}
          helperText={currentPasswordError}
          fullWidth
        />

        <TextField
          componentName="new-password"
          type="password"
          variant="outlined"
          label="New Password"
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
          required
          error={!!newPasswordError}
          helperText={newPasswordError}
          fullWidth
        />

        <TextField
          componentName="confirm-password"
          type="password"
          variant="outlined"
          label="Confirm Password"
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          required
          error={!!confirmPasswordError}
          helperText={confirmPasswordError}
          fullWidth
        />

        <Button
          componentName="change-password"
          color="primary"
          type="submit"
          disabled={!isFormValid || loading}
          loading={loading}
        >
          Change Password
        </Button>
      </Box>
    </Container>
  );
};
